import { Select, Tabs } from "antd";
import React, { useEffect } from "react";
import JobParameters from "./JobParameters";
import JobParametersM500 from "./JobParametersM500";
import LeadTorch from "./LeadTorch";
import PipeParametersTable from "./PipeParametersTable";
import ProjectInformation from "./ProjectInformation";
import SoftwareVersions from "./SoftwareVersions";
import SoftwareVersionsM500 from "./SoftwareVersionsM500";
import TrailTorch from "./TrailTorch";
import WeldParameters from "./WeldParameters";
import AllConfiguration from "./allConfiguration";

const WeldParameterTab = ({ setActiveTab, selectedMachineJob, activeTab }) => {
  const [selectedPipeParameters, setSelectedPipeParameters] =
    React.useState(null);
  const [showPipeParametersTable, setShowPipeParametersTable] =
    React.useState(false);

  // Convert numeric activeTab to corresponding tab key based on machine type
  const getActiveKey = () => {
    if (showPipeParametersTable) return "PipeParameters";
    if (!selectedMachineJob) return "";

    const machine = selectedMachineJob.machine;

    // P600Z tabs mapping
    if (machine === "p600z") {
      switch (activeTab) {
        case "1":
          return "AllConfiguration";
        case "2":
          return "LeadTorch";
        case "3":
          return "TrailTorch";
        default:
          return "AllConfiguration";
      }
    }
    // P625 tabs mapping
    else if (machine === "p625") {
      switch (activeTab) {
        case "1":
          return "JobParameters";
        case "2":
          return "WeldParameters";
        case "3":
          return "SoftwareVersions";
        default:
          return "JobParameters";
      }
    }
    // M500 tabs mapping
    else if (machine === "m500") {
      switch (activeTab) {
        case "1":
          return "SoftwareVersions";
        case "2":
          return "ProjectInformation";
        case "3":
          return "JobParameters";
        default:
          return "SoftwareVersions";
      }
    }
    return "";
  };

  const activeKey = getActiveKey();

  // While in Viewing Pipe Parameters changing the job number will reset the tab
  useEffect(() => {
    setActiveTab("1");
    setShowPipeParametersTable(false);
    setSelectedPipeParameters(null);
  }, [selectedMachineJob]);

  const handleTabChange = (key) => {
    if (!selectedMachineJob) return;

    // If switching from PipeParameters tab, reset dropdown
    if (key !== "PipeParameters") {
      setShowPipeParametersTable(false);
      setSelectedPipeParameters(null);
    }

    const machine = selectedMachineJob.machine;

    // Convert tab key back to numeric activeTab
    if (machine === "p600z") {
      switch (key) {
        case "AllConfiguration":
          setActiveTab("1");
          break;
        case "LeadTorch":
          setActiveTab("2");
          break;
        case "TrailTorch":
          setActiveTab("3");
          break;
        default:
          setActiveTab("1");
      }
    } else if (machine === "p625") {
      switch (key) {
        case "JobParameters":
          setActiveTab("1");
          break;
        case "WeldParameters":
          setActiveTab("2");
          break;
        case "SoftwareVersions":
          setActiveTab("3");
          break;
        default:
          setActiveTab("1");
      }
    } else if (machine === "m500") {
      switch (key) {
        case "SoftwareVersions":
          setActiveTab("1");
          break;
        case "ProjectInformation":
          setActiveTab("2");
          break;
        case "JobParameters":
          setActiveTab("3");
          break;
        default:
          setActiveTab("1");
      }
    }
  };

  const handlePipeParametersChange = (value) => {
    setSelectedPipeParameters(value);
    setShowPipeParametersTable(true);
  };

  // Helper to get all PIPE PARAMETERS display names
  const getPipeParameterDisplayNames = () => {
    if (!selectedMachineJob) return [];
    const jobData = selectedMachineJob.jobData;
    if (!jobData || !jobData.parsed_json_data) return [];

    return Object.entries(jobData.parsed_json_data)
      .filter(
        ([key, value]) =>
          key.startsWith("PIPE PARAMETERS;") && value.displayName
      )
      .map(([key, value]) => ({
        key,
        displayName: value.displayName,
      }));
  };

  const getTabItems = () => {
    if (!selectedMachineJob) return [];

    const machine = selectedMachineJob.machine;
    const jobData = selectedMachineJob.jobData;

    let baseTabs = [];

    if (machine === "p600z") {
      baseTabs = [
        {
          key: "AllConfiguration",
          label: "All Configuration",
          children: showPipeParametersTable ? null : (
            <AllConfiguration
              selectedJob={machine.toUpperCase()}
              weldParameters={jobData.parsed_json_data}
            />
          ),
        },
        {
          key: "LeadTorch",
          label: "Lead Torch",
          children: showPipeParametersTable ? null : (
            <LeadTorch
              selectedJob={machine.toUpperCase()}
              weldParameters={jobData.parsed_json_data}
            />
          ),
        },
        {
          key: "TrailTorch",
          label: "Trail Torch",
          children: showPipeParametersTable ? null : (
            <TrailTorch
              selectedJob={machine.toUpperCase()}
              weldParameters={jobData.parsed_json_data}
            />
          ),
        },
      ];
    } else if (machine === "p625") {
      baseTabs = [
        {
          key: "JobParameters",
          label: "Job Parameters",
          children: showPipeParametersTable ? null : (
            <JobParameters
              selectedJob={machine.toUpperCase()}
              jobParameters={jobData.parsed_json_data["JOB PARAMETERS"]}
            />
          ),
        },
        {
          key: "WeldParameters",
          label: "Weld Parameters",
          children: showPipeParametersTable ? null : (
            <WeldParameters
              selectedJob={machine.toUpperCase()}
              weldParameters={jobData.parsed_json_data["WELD PARAMETERS"]}
            />
          ),
        },
        {
          key: "SoftwareVersions",
          label: "Software Versions",
          children: showPipeParametersTable ? null : (
            <SoftwareVersions
              selectedJob={machine.toUpperCase()}
              softwareVersions={jobData.parsed_json_data["SOFTWARE VERSIONS"]}
            />
          ),
        },
      ];
    } else if (machine === "m500") {
      baseTabs = [
        {
          key: "SoftwareVersions",
          label: "Software Versions",
          children: showPipeParametersTable ? null : (
            <SoftwareVersionsM500
              selectedJob={machine.toUpperCase()}
              softwareVersions={jobData.parsed_json_data["SOFTWARE VERSIONS"]}
            />
          ),
        },
        {
          key: "ProjectInformation",
          label: "Project Information",
          children: showPipeParametersTable ? null : (
            <ProjectInformation
              selectedJob={machine.toUpperCase()}
              projectInfo={jobData.parsed_json_data["PROJECT INFORMATION"]}
            />
          ),
        },
        {
          key: "JobParameters",
          label: "Job Parameters",
          children: showPipeParametersTable ? null : (
            <JobParametersM500
              selectedJob={machine.toUpperCase()}
              jobParameters={jobData.parsed_json_data["JOB PARAMETERS"]}
            />
          ),
        },
        {
          key: "PipeParameters",
          label: (
            <Select
              placeholder="Select Pipe Parameter"
              className="weld-parameter-screen-select"
              onChange={handlePipeParametersChange}
              value={selectedPipeParameters}
              onClick={(e) => e.stopPropagation()}
              dropdownStyle={{ zIndex: 2000 }}
            >
              {getPipeParameterDisplayNames().map((option) => (
                <Select.Option key={option.key} value={option.key}>
                  {option.displayName}
                </Select.Option>
              ))}
            </Select>
          ),
          children:
            showPipeParametersTable && selectedPipeParameters ? (
              <div style={{ marginTop: "20px" }}>
                <PipeParametersTable
                  pipeParameter={selectedPipeParameters}
                  pipeParameterData={
                    selectedMachineJob.jobData.parsed_json_data[
                      selectedPipeParameters
                    ]
                  }
                />
              </div>
            ) : null,
        },
      ];
    }

    return baseTabs;
  };

  return (
    <div>
      {selectedMachineJob && (
        <Tabs
          activeKey={activeKey}
          items={getTabItems()}
          className="custom-tab weld-parameter-custom-tab"
          destroyInactiveTabPane={true}
          onChange={handleTabChange}
        />
      )}
    </div>
  );
};

export default WeldParameterTab;
